/**
 * API Service for connecting to the Margin Risk Management backend
 */

import { 
  ApiPortfolioSummary, 
  ApiPortfolioDetail, 
  ApiPortfolioCount, 
  MarginAlert, 
  AlertsCountSummary,
  ApiCommentsListResponse,
  CommentCreateRequest,
  ReplyCreateRequest,
  ApiCommentResponse
} from '@/types';
import { AuthUser, LoginRequest, LoginResponse, ChangePasswordRequest, ApiError } from '@/types/auth';
import { ApiEffectiveMarginResponse } from '@/types/effectiveMargin';
import {
  ARMAssignment,
  ARMAssignmentListResponse,
  ARMAssignmentCreate,
  UserOption,
  ICOverride,
  ICOverrideListResponse,
  ICOverrideCreate,
  ICOverrideUpdate,
  PortfolioOption
} from '../types/admin';

const API_BASE_URL = '';

class ApiService {
  private isRefreshing = false;
  private refreshSubscribers: Array<() => void> = [];

  private async fetchWithTimeout(url: string, options: RequestInit = {}, timeout = 10000): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        credentials: 'include', // Include httpOnly cookies
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
      clearTimeout(timeoutId);

      // Handle 401 Unauthorized - attempt token refresh
      if (response.status === 401 && !url.includes('/auth/login') && !url.includes('/auth/refresh') && !url.includes('/auth/sso')) {
        const shouldRetry = await this.handleUnauthorized();
        if (shouldRetry) {
          // Retry the original request after token refresh
          return this.fetchWithTimeout(url, options, timeout);
        }
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      // Provide better error message for aborted requests
      if (error instanceof Error && error.name === 'AbortError') {
        const timeoutSeconds = timeout / 1000;
        throw new Error(`Request timed out after ${timeoutSeconds} seconds. Please try again.`);
      }
      throw error;
    }
  }

  private async handleUnauthorized(): Promise<boolean> {
    if (this.isRefreshing) {
      // Wait for the current refresh to complete
      return new Promise((resolve) => {
        this.refreshSubscribers.push(() => resolve(true));
      });
    }

    this.isRefreshing = true;

    try {
      const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (refreshResponse.ok) {
        // Token refreshed successfully
        this.isRefreshing = false;
        this.refreshSubscribers.forEach((callback) => callback());
        this.refreshSubscribers = [];
        return true;
      } else {
        // Refresh failed - don't redirect here, let React Router handle it
        this.isRefreshing = false;
        this.refreshSubscribers = [];
        return false;
      }
    } catch (error) {
      this.isRefreshing = false;
      this.refreshSubscribers = [];
      return false;
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `Request failed: ${response.statusText}`;

      try {
        const errorData: ApiError = await response.json();
        errorMessage = errorData.detail || errorMessage;
      } catch {
        // If JSON parsing fails, use the default error message
      }

      const error = new Error(errorMessage) as Error & { status: number };
      error.status = response.status;
      throw error;
    }

    return response.json();
  }

  // ============================================================================
  // Authentication Methods
  // ============================================================================

  async login(email: string, password: string): Promise<AuthUser> {
    // Use longer timeout for login (30 seconds) due to bcrypt verification and audit logging
    const response = await this.fetchWithTimeout(
      `${API_BASE_URL}/auth/login`,
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      },
      30000 // 30 second timeout for login
    );

    const data: LoginResponse = await this.handleResponse(response);
    return data.user;
  }

  async loginWithSSO(idToken: string): Promise<AuthUser> {
    // Use longer timeout for SSO login (30 seconds) due to token validation
    const response = await this.fetchWithTimeout(
      `${API_BASE_URL}/auth/sso`,
      {
        method: 'POST',
        body: JSON.stringify({ id_token: idToken }),
      },
      30000 // 30 second timeout for SSO login
    );

    const data: LoginResponse = await this.handleResponse(response);
    return data.user;
  }

  async loginWithLocalDevBypass(): Promise<AuthUser> {
    const response = await this.fetchWithTimeout(
      `${API_BASE_URL}/auth/dev-login`,
      { method: 'POST' },
      30000,
    );

    const data: LoginResponse = await this.handleResponse(response);
    return data.user;
  }

  async logout(): Promise<void> {
    const response = await this.fetchWithTimeout(
      `${API_BASE_URL}/auth/logout`,
      {
        method: 'POST',
      }
    );

    if (!response.ok) {
      throw new Error('Logout failed');
    }
  }

  async getCurrentUser(): Promise<AuthUser> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/auth/me`);
    return this.handleResponse(response);
  }

  async changePassword(newPassword: string): Promise<void> {
    const response = await this.fetchWithTimeout(
      `${API_BASE_URL}/auth/change-password`,
      {
        method: 'POST',
        body: JSON.stringify({
          new_password: newPassword,
        }),
      }
    );

    if (!response.ok) {
      const errorData: ApiError = await response.json();
      throw new Error(errorData.detail || 'Password change failed');
    }
  }

  async refreshToken(): Promise<void> {
    const response = await this.fetchWithTimeout(
      `${API_BASE_URL}/auth/refresh`,
      {
        method: 'POST',
      }
    );

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }
  }

  // ============================================================================
  // Health Check
  // ============================================================================

  async checkHealth(): Promise<{ status: string; timestamp: string; database: string }> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/health`);
    return this.handleResponse(response);
  }

  // ============================================================================
  // Portfolio Methods
  // ============================================================================

  async getPortfolios(): Promise<ApiPortfolioSummary[]> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios`);
    return this.handleResponse(response);
  }

  async getPortfolioDetail(portfolioId: string): Promise<ApiPortfolioDetail> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios/${portfolioId}`);
    return this.handleResponse(response);
  }

  async getEffectiveMargin(portfolioId: string): Promise<ApiEffectiveMarginResponse> {
    const response = await this.fetchWithTimeout(
      `${API_BASE_URL}/api/portfolios/${portfolioId}/effective-margin`,
    );
    return this.handleResponse(response);
  }

  async getPortfolioCount(): Promise<ApiPortfolioCount> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios/count`);
    return this.handleResponse(response);
  }

  async getAllPortfolioDetails(): Promise<ApiPortfolioDetail[]> {
    const response = await this.fetchWithTimeout(
      `${API_BASE_URL}/api/portfolios/all-positions`,
      {},
      60000
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Alert Methods
  // ============================================================================

  async getAlerts(): Promise<MarginAlert[]> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/alerts`);
    return this.handleResponse(response);
  }

  async getAlertByPortfolio(portfolioId: string): Promise<MarginAlert> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/alerts/${portfolioId}`);
    return this.handleResponse(response);
  }

  async getAlertsCountSummary(): Promise<AlertsCountSummary> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/alerts/count/summary`);
    return this.handleResponse(response);
  }

  async acknowledgeAlert(portfolioId: string): Promise<void> {
    const response = await this.fetchWithTimeout(
      `${API_BASE_URL}/api/alerts/${portfolioId}/acknowledge`,
      {
        method: 'POST',
      }
    );
    return this.handleResponse(response);
  }

  // ============================================================================
  // Comment Methods
  // ============================================================================

  async getCommentCount(portfolioId: string): Promise<{ portfolio_id: string; count: number }> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios/${portfolioId}/comments/count`);
    return this.handleResponse(response);
  }

  async getCommentCountsBatch(portfolioIds: string[]): Promise<{ portfolio_id: string; count: number }[]> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios/comments/counts/batch`, {
      method: 'POST',
      body: JSON.stringify(portfolioIds)
    });
    return this.handleResponse(response);
  }

  async getPortfolioComments(portfolioId: string): Promise<ApiCommentsListResponse> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios/${portfolioId}/comments`);
    return this.handleResponse(response);
  }

  async createComment(portfolioId: string, data: CommentCreateRequest): Promise<ApiCommentResponse> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios/${portfolioId}/comments`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async createReply(portfolioId: string, commentId: string, data: ReplyCreateRequest): Promise<ApiCommentResponse> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios/${portfolioId}/comments/${commentId}/reply`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async updateComment(portfolioId: string, commentId: string, data: CommentCreateRequest): Promise<ApiCommentResponse> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios/${portfolioId}/comments/${commentId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async deleteComment(portfolioId: string, commentId: string): Promise<void> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios/${portfolioId}/comments/${commentId}`, {
      method: 'DELETE'
    });
    return this.handleResponse(response);
  }

  async getAllComments(filters?: {
    rmUserId?: string;
    category?: string;
    isMarginTrading?: boolean;
    startDate?: string;
    endDate?: string;
  }): Promise<ApiCommentsListResponse[]> {
    const params = new URLSearchParams();

    if (filters?.rmUserId) {
      params.append('rm_user_id', filters.rmUserId);
    }
    if (filters?.category) {
      params.append('category', filters.category);
    }
    if (filters?.isMarginTrading !== undefined) {
      params.append('is_margin_trading', filters.isMarginTrading.toString());
    }
    if (filters?.startDate) {
      params.append('start_date', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('end_date', filters.endDate);
    }

    const queryString = params.toString();
    const url = queryString
      ? `${API_BASE_URL}/api/portfolios/all?${queryString}`
      : `${API_BASE_URL}/api/portfolios/all`;

    const response = await this.fetchWithTimeout(url);
    return this.handleResponse(response);
  }

  async getRMsList(): Promise<Array<{ rm_id: string; rm_name: string; rm_email: string }>> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/portfolios/rms`);
    return this.handleResponse(response);
  }

  // ============================================================================
  // Admin User Management Methods
  // ============================================================================

  async getUsers(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    role?: string;
    user_type?: string;
    is_active?: boolean;
  }): Promise<{
    users: AuthUser[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.role) queryParams.append('role', params.role);
      if (params.user_type) queryParams.append('user_type', params.user_type);
      if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    }

    const url = `${API_BASE_URL}/admin/users${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.fetchWithTimeout(url);
    return this.handleResponse(response);
  }

  async createUser(data: {
    email: string;
    first_name?: string;
    last_name?: string;
    role: string;
    account_scope?: AuthUser['account_scope'];
    user_type: string;
    password?: string;
  }): Promise<AuthUser> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/users`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async updateUser(userId: number, data: {
    first_name?: string;
    last_name?: string;
    role?: string;
    account_scope?: AuthUser['account_scope'];
    user_type?: string;
    is_active?: boolean;
  }): Promise<AuthUser> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async deactivateUser(userId: number): Promise<void> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'DELETE'
    });
    return this.handleResponse(response);
  }

  async activateUser(userId: number): Promise<void> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/users/${userId}/activate`, {
      method: 'POST'
    });
    return this.handleResponse(response);
  }

  async resetUserPassword(userId: number): Promise<{ password: string; message: string }> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/users/${userId}/reset-password`, {
      method: 'POST'
    });
    return this.handleResponse(response);
  }

  // ============================================================================
  // ARM Assignment Methods
  // ============================================================================

  async getARMAssignments(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    arm_user_id?: number;
    rm_user_id?: number;
    is_active?: boolean;
  }): Promise<ARMAssignmentListResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.arm_user_id) queryParams.append('arm_user_id', params.arm_user_id.toString());
      if (params.rm_user_id) queryParams.append('rm_user_id', params.rm_user_id.toString());
      if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    }

    const url = `${API_BASE_URL}/admin/arm-assignments${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.fetchWithTimeout(url);
    return this.handleResponse(response);
  }

  async createARMAssignment(data: ARMAssignmentCreate): Promise<ARMAssignment> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/arm-assignments`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async deactivateARMAssignment(assignmentId: number): Promise<void> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/arm-assignments/${assignmentId}`, {
      method: 'DELETE'
    });
    return this.handleResponse(response);
  }

  async activateARMAssignment(assignmentId: number): Promise<void> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/arm-assignments/${assignmentId}/activate`, {
      method: 'POST'
    });
    return this.handleResponse(response);
  }

  async getARMUsers(): Promise<UserOption[]> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/users/arms`);
    return this.handleResponse(response);
  }

  async getRMUsers(): Promise<UserOption[]> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/users/rms`);
    return this.handleResponse(response);
  }

  // ============================================================================
  // IC Override Methods
  // ============================================================================

  async getICOverrides(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    is_active?: boolean;
    portfolio_id?: string;
  }): Promise<ICOverrideListResponse> {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.page_size) queryParams.append('page_size', params.page_size.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
      if (params.portfolio_id) queryParams.append('portfolio_id', params.portfolio_id);
    }

    const url = `${API_BASE_URL}/admin/ic-overrides${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.fetchWithTimeout(url);
    return this.handleResponse(response);
  }

  async createICOverride(data: ICOverrideCreate): Promise<ICOverride> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/ic-overrides`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async updateICOverride(overrideId: number, data: ICOverrideUpdate): Promise<ICOverride> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/ic-overrides/${overrideId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async deactivateICOverride(overrideId: number): Promise<void> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/ic-overrides/${overrideId}`, {
      method: 'DELETE'
    });
    return this.handleResponse(response);
  }

  async activateICOverride(overrideId: number): Promise<ICOverride> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/ic-overrides/${overrideId}/activate`, {
      method: 'POST'
    });
    return this.handleResponse(response);
  }

  async getICUsers(): Promise<UserOption[]> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/admin/users/ics`);
    return this.handleResponse(response);
  }

  async getAdminPortfolios(search?: string): Promise<PortfolioOption[]> {
    const queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);

    const url = `${API_BASE_URL}/admin/portfolios${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.fetchWithTimeout(url);
    return this.handleResponse(response);
  }

  // ============================================================================
  // Margin Trading Override Methods
  // ============================================================================

  async getMarginTradingOverrides(): Promise<any[]> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/admin/margin-trading-overrides`);
    return this.handleResponse(response);
  }

  async setMarginTradingOverride(data: {
    portfolio_id_lig: string;
    override_status: string;
    notes?: string;
    overridden_by: string;
  }): Promise<any> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/admin/margin-trading-overrides`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }

  async deleteMarginTradingOverride(portfolioId: string): Promise<void> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/admin/margin-trading-overrides/${portfolioId}`, {
      method: 'DELETE'
    });
    return this.handleResponse(response);
  }

  // ============================================================================
  // Email Template Methods
  // ============================================================================

  async getEmailTemplates(): Promise<any[]> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/admin/email-templates`);
    return this.handleResponse(response);
  }

  async getEmailTemplate(templateId: number): Promise<any> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/admin/email-templates/${templateId}`);
    return this.handleResponse(response);
  }

  async updateEmailTemplate(templateId: number, data: {
    subject_template: string;
    body_template: string;
  }): Promise<any> {
    const response = await this.fetchWithTimeout(`${API_BASE_URL}/api/admin/email-templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return this.handleResponse(response);
  }
}

export const apiService = new ApiService();

// Convenience exports for specific services
export const emailTemplateService = {
  getAll: () => apiService.getEmailTemplates(),
  get: (id: number) => apiService.getEmailTemplate(id),
  update: (id: number, data: { subject_template: string; body_template: string }) => 
    apiService.updateEmailTemplate(id, data)
};

export default apiService;
